import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  Memory as MemoryIcon,
  Chat as ChatIcon,
  Code as CodeIcon,
  Storage as StorageIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  ChartData,
  ChartOptions,
} from "chart.js";
import { useAlert } from "../../context/AlertContext";
import PageHeader from "../../components/common/PageHeader";
import RecentEvaluations from "./RecentEvaluations";
import api from "../../services/api";
import { DashboardData, RecentEvaluation, StatCardProps } from "../../types";
import { Theme } from "@mui/material/styles";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
);

// Stat Card Component
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  subtext,
  change,
  to,
}) => {
  const content = (
    <Card sx={{ height: "100%", borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              {value}
            </Typography>
            {subtext && (
              <Typography variant="body2" color="text.secondary">
                {subtext}
              </Typography>
            )}
            {change !== undefined && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Chip
                  size="small"
                  label={`${change > 0 ? "+" : ""}${change}%`}
                  color={change >= 0 ? "success" : "error"}
                  sx={{ fontSize: "0.75rem", height: 20 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  vs last month
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  return to ? (
    <RouterLink to={to} style={{ textDecoration: "none" }}>
      {content}
    </RouterLink>
  ) : (
    content
  );
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const alert = useAlert();
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    stats: {
      promptCount: 0,
      schemaCount: 0,
      testCount: 0,
      evaluationCount: 0,
      successRate: 0,
    },
    recentEvaluations: [],
    evaluationsByDay: {
      labels: [],
      data: [],
    },
    resultDistribution: {
      labels: [],
      data: [],
    },
  });

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.dashboard.getSummary();
      setDashboardData(response.data);
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      let errorMessage = "Failed to load dashboard data";

      if (error.response?.data?.error) {
        errorMessage += `: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      alert.error(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Handle refresh button click
  const handleRefresh = () => {
    fetchDashboardData();
  };

  // Chart data for evaluations trend
  const trendChartData: ChartData<"line"> = {
    labels: dashboardData.evaluationsByDay.labels,
    datasets: [
      {
        label: "Evaluations",
        data: dashboardData.evaluationsByDay.data,
        fill: true,
        backgroundColor: `${theme.palette.primary.main}22`,
        borderColor: theme.palette.primary.main,
        tension: 0.4,
      },
    ],
  };

  // Chart options for evaluations trend
  const trendChartOptions: ChartOptions<"line"> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary as string,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 3,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: theme.palette.divider,
        },
      },
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
  };

  // Chart data for result distribution
  const distributionChartData: ChartData<"doughnut"> = {
    labels: dashboardData.resultDistribution.labels,
    datasets: [
      {
        data: dashboardData.resultDistribution.data,
        backgroundColor: [
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.error.main,
          theme.palette.grey[400],
        ],
        borderWidth: 0,
      },
    ],
  };

  // Chart options for result distribution
  const distributionChartOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary as string,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        boxWidth: 10,
        boxHeight: 10,
        boxPadding: 3,
        usePointStyle: true,
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = (context.raw as number) || 0;
            const total = (context.dataset.data as number[]).reduce(
              (a: number, b: number) => a + b,
              0,
            );
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "70%",
  };

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Monitor your evaluations and system performance"
      />

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Prompts"
            value={dashboardData.stats.promptCount}
            icon={<ChatIcon />}
            color="primary"
            to="/prompts"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Tests"
            value={dashboardData.stats.testCount}
            icon={<MemoryIcon />}
            color="warning"
            to="/tests"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Success Rate"
            value={`${dashboardData.stats.successRate || 0}%`}
            icon={<AssessmentIcon />}
            color="success"
            to="/evaluations"
          />
        </Grid>
      </Grid>

      {/* Main Dashboard Content */}
      <Grid container spacing={3}>
        {/* Recent Evaluations */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: "100%", borderRadius: 2 }}>
            <CardHeader
              title="Recent Evaluations"
              action={
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Refresh">
                    <IconButton size="small" onClick={handleRefresh}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    size="small"
                    component={RouterLink}
                    to="/evaluations"
                    sx={{ ml: 1 }}
                  >
                    View All
                  </Button>
                </Box>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <RecentEvaluations
                evaluations={dashboardData.recentEvaluations}
                loading={loading}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: "100%", borderRadius: 2 }}>
            <CardHeader title="Quick Actions" />
            <Divider />
            <CardContent>
              <List sx={{ width: "100%" }}>
                <ListItem
                  button
                  component={RouterLink}
                  to="/prompts/new"
                  sx={{ borderRadius: 1, mb: 1 }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: "secondary.light",
                        color: "secondary.main",
                      }}
                    >
                      <ChatIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Create Prompt"
                    secondary="Add a new system or user prompt"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: 2 }}>
            <CardHeader title="Evaluations Trend" subheader="Last 7 days" />
            <Divider />
            <CardContent>
              <Box sx={{ height: 300, position: "relative" }}>
                {loading ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <Line data={trendChartData} options={trendChartOptions} />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: 2 }}>
            <CardHeader
              title="Result Distribution"
              subheader="All evaluations"
            />
            <Divider />
            <CardContent>
              <Box
                sx={{
                  height: 300,
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ width: "80%", maxWidth: 400 }}>
                    <Doughnut
                      data={distributionChartData}
                      options={distributionChartOptions}
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
